import { useEffect, useMemo, useState } from 'react'
import { Alert, Spinner, Typography } from '@material-tailwind/react'
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import AgamiFormV2 from './AgamiFormV2'
import Container from '../components/Container'
import GoBack from '../components/GoBack'
import { useSessionContext } from '../contexts/SessionContext'
import { usePageTitle } from '../hooks/toolkit'
import { useFeatureFlag } from '../hooks/authorization'
import { FEATURE_FLAGS } from '../utils/constants/features.constants'
import { getUserGeoXpSpotCount } from '../utils/helpers/user-spot.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'

/**
 * Creation page of the v2 form (06.1). The flag guard lives HERE, on the route
 * target — never inside a form: neither view contains logic about the other,
 * which is what makes the coexistence releasable. 06.4 adds the real entry
 * points; until then the route exists for whoever carries the flag.
 *
 * `?proposeTo=<experienceId>` opens the form in propose mode: one fixed
 * destination, no visibility, no media — the shared-experience view (06.4)
 * will link here.
 */
const UploadAgamiV2: React.FC = () => {
  const { t } = useTranslation()
  usePageTitle('Upload Spot | Agami')
  const { darkMode } = useDarkMode()
  const { loading, user } = useSessionContext()
  const navigate = useNavigate()

  const ondeFlag = useFeatureFlag(FEATURE_FLAGS.ONDE)

  const [searchParams] = useSearchParams()
  // Where to go back to (06.4): whoever opened the form says so via navigation
  // state — the experience view, the shared view — because the user may also
  // arrive from the upload menu, and then the return is elsewhere. Never
  // reconstructed.
  const location = useLocation()
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo
  const proposeTo = searchParams.get('proposeTo')
  const addToExperience = searchParams.get('addToExperience')
  const mode = proposeTo ? 'propose' : 'author'

  const spotLimit = user?.uploadLimit
  const [uploadLimitReached, setUploadLimitReached] = useState(false)
  const [countLoading, setCountLoading] = useState(true)

  useEffect(() => {
    if (loading || !user?.id) {
      return
    }

    // A proposal is written by the RPC and belongs to the experience's owner
    // (D9): the proposer's own upload limit is irrelevant to it.
    if (mode === 'propose') {
      setCountLoading(false)
      return
    }

    getUserGeoXpSpotCount().then(({ count }) => {
      setUploadLimitReached((count ?? 0) >= Number(spotLimit))
      setCountLoading(false)
    })
  }, [loading, user, mode, spotLimit])

  const isLoading = useMemo(() => loading || countLoading, [loading, countLoading])

  if (!loading && !ondeFlag) {
    return (
      <Navigate
        to={{ pathname: '/upload/spot', search: location.search, hash: location.hash }}
        state={location.state}
        replace
      />
    )
  }

  return (
    <Container direction="col">
      <div className="mb-2 self-start">
        <GoBack />
      </div>
      <Typography variant="lead" className="text-primary dark:text-white" data-testid="upload-agami-v2-title">
        {mode === 'propose' ? t('uploadV2.propose.title') : t('upload.title')}
      </Typography>

      {isLoading && (
        <div className="w-full my-10">
          <Spinner className="h-12 w-12 mx-auto" />
        </div>
      )}

      {!isLoading && uploadLimitReached && (
        <div className="my-10 text-center">
          <Alert variant={darkMode ? 'outlined' : 'ghost'} color="orange">
            <Typography variant="paragraph">
              {t('upload.uploadLimitReached')} You can check them in your{' '}
              <span className="font-bold underline">
                <Link to="/">{t('navigation.dashboard')}</Link>
              </span>
              .
            </Typography>
          </Alert>
        </div>
      )}

      {!isLoading && !uploadLimitReached && (
        <AgamiFormV2
          mode={mode}
          proposeToExperienceId={proposeTo ?? undefined}
          onSaved={({ created }) => {
            if (mode === 'propose') {
              if (returnTo) navigate(returnTo)
              else navigate(-1)
              return
            }
            if (created) {
              navigate(returnTo ?? (addToExperience ? `/experiences/${addToExperience}` : '/'))
            }
          }}
          onCancel={returnTo ? () => navigate(returnTo) : undefined}
        />
      )}
    </Container>
  )
}

export default UploadAgamiV2
